/**
 * Camera Finder quiz.
 * Four taps -> a recommendation, computed entirely client-side; the full
 * result always shows with no gate. Capture layers, all fire-and-forget:
 * answers ride on the cart as attributes; completions (anonymous) and
 * follow-up leads (name/email/phone) post to the optional results webhook;
 * the follow-up form itself is a Shopify contact form that emails the
 * store inbox with the summary.
 */
(() => {
  const CHECK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-11" stroke="currentColor"/></svg>';


  const ANSWER_LABELS = {
    use: { calving: 'Calving/lambing shed', horses: 'Horses & stables', security: 'Yard & security', remote: 'Remote/off-grid spot' },
    power: { mains: 'Mains power available', none: 'No power at the spot' },
    wifi: { wifi: 'Good WiFi', none: 'No/patchy WiFi', unsure: 'WiFi unsure' },
    distance: { close: 'Close range', yard: 'Across a yard', far: 'Long range/zoom' },
    urgency: { asap: 'Needs it ASAP', soon: 'Next few weeks', research: 'Just researching' },
    scale: { one: 'One camera', few: 'Two-three spots', site: 'Whole site' },
  };
  const REC_LABELS = { solar: 'Solar 360 Mini', static: 'Static Camera', ptz: 'Farmstream 360' };

  const safeParse = (s) => {
    try {
      return s ? JSON.parse(s) : null;
    } catch (e) {
      return null;
    }
  };

  function initQuiz(root) {
    if (!root || root.dataset.quizInit) return;
    root.dataset.quizInit = 'true';

    const steps = Array.from(root.querySelectorAll('.cquiz__step'));
    const result = root.querySelector('.cquiz__result');
    const dots = Array.from(root.querySelectorAll('.cquiz__progress-dot'));
    const progressLabel = root.querySelector('.cquiz__progress-label');
    const progress = root.querySelector('.cquiz__progress');
    const backBtn = root.querySelector('.cquiz__back');
    const answers = {};
    let current = 0;

    const PRODUCTS = {
      solar: safeParse(root.dataset.solar),
      static: safeParse(root.dataset.static),
      ptz: safeParse(root.dataset.ptz),
    };

    function show(index, moveFocus) {
      steps.forEach((s, i) => (s.hidden = i !== index));
      result.hidden = true;
      if (backBtn) backBtn.hidden = index === 0;
      if (progress) progress.hidden = false;
      dots.forEach((d, i) => d.classList.toggle('cquiz__progress-dot--done', i <= index));
      if (progressLabel) progressLabel.textContent = 'Question ' + (index + 1) + ' of ' + steps.length;
      current = index;
      if (moveFocus) {
        const q = steps[index].querySelector('.cquiz__question');
        if (q) {
          q.setAttribute('tabindex', '-1');
          q.focus();
        }
      }
    }

    function recommend() {
      const a = answers;
      const noPower = a.power === 'none';
      const needs4g = a.wifi !== 'wifi';
      let key;
      const why = [];
      let alt = '';

      if (noPower) {
        key = 'solar';
        why.push('No mains power needed — the solar panel and built-in battery keep it running');
        why.push('4G connected with the built-in multi-network SIM (EE, O2, Three or Vodafone)');
        why.push('Pan and tilt from your phone to cover the whole area');
        if (a.distance === 'far') {
          alt = 'Need serious long-range zoom? If you can get mains power to the spot, the Farmstream 360’s 36x optical zoom is the stronger choice.';
        }
      } else if (a.distance === 'close') {
        key = 'static';
        why.push('A fixed view is perfect for watching one pen, stable or doorway');
        why.push('Clear night vision for round-the-clock checks');
        why.push('The most affordable way to get eyes on the spot that matters');
        alt = 'Want to pan around or zoom in on details across a bigger space? Step up to the Farmstream 360.';
      } else {
        key = 'ptz';
        why.push('Pan, tilt and 36x optical zoom cover a whole shed or yard with one camera');
        if (a.use === 'security') {
          why.push('Auto-tracking follows movement, with motion alerts to your phone');
        } else {
          why.push('Zoom right in to check on an animal without leaving the house');
        }
        why.push('8MP 4K picture with 80m night vision');
      }

      if (needs4g) {
        why.push('No WiFi needed — works on the built-in multi-network 4G SIM');
      } else if (key !== 'solar') {
        why.push('WiFi and 4G models available — use your WiFi and skip the data plan');
      }

      return { key, why, alt, needs4g };
    }

    function showResultShell() {
      steps.forEach((s) => (s.hidden = true));
      if (progress) progress.hidden = true;
      if (backBtn) backBtn.hidden = true;
      result.hidden = false;
    }

    function renderResult() {
      const rec = recommend();
      const product = PRODUCTS[rec.key];
      const card = root.querySelector('.cquiz__result-card');
      const media = root.querySelector('.cquiz__result-media');
      const title = root.querySelector('.cquiz__result-title');
      const price = root.querySelector('.cquiz__result-price');
      const whyList = root.querySelector('.cquiz__why');
      const viewBtn = root.querySelector('.cquiz__view-btn');
      const altEl = root.querySelector('.cquiz__alt:not(.cquiz__plan-note)');
      const planNote = root.querySelector('.cquiz__plan-note');

      if (card) card.hidden = false;
      if (product) {
        title.textContent = product.title;
        price.textContent = product.price ? 'From ' + product.price : '';
        viewBtn.href = product.url;
        if (product.image) {
          media.innerHTML =
            '<img src="' + product.image + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy" width="440" height="440">';
          media.hidden = false;
        } else {
          media.hidden = true;
        }
      }

      whyList.innerHTML = rec.why
        .map((w) => '<li>' + CHECK_ICON + '<span>' + w + '</span></li>')
        .join('');

      if (altEl) {
        altEl.textContent = rec.alt;
        altEl.hidden = !rec.alt;
      }
      if (planNote) planNote.hidden = !rec.needs4g;
      const urgencyNote = root.querySelector('.cquiz__urgency-note');
      if (urgencyNote) urgencyNote.hidden = answers.urgency !== 'asap';
      const scaleNote = root.querySelector('.cquiz__scale-note');
      if (scaleNote) scaleNote.hidden = !(answers.scale === 'few' || answers.scale === 'site');
      const postcodeField = root.querySelector('.cquiz__postcode');
      if (postcodeField) postcodeField.hidden = !rec.needs4g;

      // human-readable summary rides along in the follow-up form's message body
      const bodyInput = root.querySelector('.cquiz__body-input');
      if (bodyInput) {
        bodyInput.value =
          'Camera quiz enquiry - Recommendation: ' + ((PRODUCTS[rec.key] && PRODUCTS[rec.key].title) || REC_LABELS[rec.key] || rec.key) +
          '. ' + ['use', 'power', 'wifi', 'distance', 'urgency', 'scale']
            .map((k) => (ANSWER_LABELS[k] && ANSWER_LABELS[k][answers[k]]) || null)
            .filter(Boolean)
            .join('. ') +
          '. Submitted from ' + window.location.pathname + '.';
      }

      showResultShell();
      dots.forEach((d) => d.classList.add('cquiz__progress-dot--done'));

      try {
        sessionStorage.setItem('fs-camera-quiz', JSON.stringify(answers));
      } catch (e) {
        /* private browsing - fine */
      }

      recordResult(rec);

      const heading = result.querySelector('.cquiz__result-eyebrow');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }

    // Capture the completion: answers ride along as cart attributes (so a
    // later order shows them in admin), and every completion optionally
    // posts an anonymous row to the merchant's results webhook. Both are
    // fire-and-forget - failures never affect the visitor.
    let recorded = false;
    function recordResult(rec) {
      if (recorded) return;
      recorded = true;

      const summary = {
        recommendation: rec.key,
        use: answers.use || '',
        power: answers.power || '',
        wifi: answers.wifi || '',
        distance: answers.distance || '',
        urgency: answers.urgency || '',
        scale: answers.scale || '',
      };

      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'Camera quiz recommendation': summary.recommendation,
              'Camera quiz answers':
                summary.use + ' / power:' + summary.power + ' / wifi:' + summary.wifi + ' / range:' + summary.distance + ' / when:' + summary.urgency + ' / scale:' + summary.scale,
            },
          }),
        }).catch(() => {});
      } catch (e) {}

      const webhook = root.dataset.resultsWebhook;
      if (webhook) {
        const payload = JSON.stringify(
          Object.assign({ type: 'completion', completed_at: new Date().toISOString(), page: window.location.pathname }, summary)
        );
        try {
          if (!(navigator.sendBeacon && navigator.sendBeacon(webhook, payload))) {
            fetch(webhook, { method: 'POST', mode: 'no-cors', keepalive: true, body: payload }).catch(() => {});
          }
        } catch (e) {}
      }
    }

    // Advancement is driven by click (fires even on an already-checked radio,
    // so confirming an answer after going Back always works) plus change (for
    // keyboard arrow selection). The steps.indexOf guard dedupes the pair of
    // events a fresh click produces.
    function handleSelect(input) {
      const step = input.closest('.cquiz__step');
      const idx = steps.indexOf(step);
      if (idx === -1) return;
      answers[step.dataset.answer] = input.value;
      if (idx !== current) return;
      if (current < steps.length - 1) {
        show(current + 1, true);
      } else {
        renderResult();
      }
    }

    root.querySelectorAll('.cquiz__option input').forEach((input) => {
      input.addEventListener('click', () => {
        if (input.checked) handleSelect(input);
      });
      input.addEventListener('change', () => handleSelect(input));
    });

    if (backBtn) {
      backBtn.addEventListener('click', () => show(Math.max(0, current - 1), true));
    }

    const leadForm = root.querySelector('.cquiz__lead-form');
    if (leadForm) {
      leadForm.addEventListener('submit', () => {
        const webhook = root.dataset.resultsWebhook;
        if (!webhook) return;
        const val = (sel) => {
          const el = leadForm.querySelector(sel);
          return el ? el.value : '';
        };
        const rec = recommend();
        const payload = JSON.stringify({
          type: 'lead',
          submitted_at: new Date().toISOString(),
          page: window.location.pathname,
          name: val('input[autocomplete="name"]'),
          email: val('input[type="email"]'),
          phone: val('input[type="tel"]'),
          recommendation: rec.key,
          use: answers.use || '',
          power: answers.power || '',
          wifi: answers.wifi || '',
          distance: answers.distance || '',
          urgency: answers.urgency || '',
          scale: answers.scale || '',
          postcode: val('input[autocomplete="postal-code"]'),
        });
        try {
          if (!(navigator.sendBeacon && navigator.sendBeacon(webhook, payload))) {
            fetch(webhook, { method: 'POST', mode: 'no-cors', keepalive: true, body: payload }).catch(() => {});
          }
        } catch (e) {}
      });
      // survive a server validation error: Shopify re-renders without custom
      // field values, so stash the postcode and refill it on restore
      leadForm.addEventListener('submit', () => {
        const pc = leadForm.querySelector('input[autocomplete="postal-code"]');
        if (pc && pc.value) {
          try {
            sessionStorage.setItem('fs-camera-quiz-pc', pc.value);
          } catch (e) {}
        }
      });
    }

    const restart = root.querySelector('.cquiz__restart');
    if (restart) {
      restart.addEventListener('click', () => {
        root.querySelectorAll('.cquiz__option input').forEach((i) => (i.checked = false));
        Object.keys(answers).forEach((k) => delete answers[k]);
        // a server-rendered "saved" confirmation belongs to the previous
        // recommendation - hide the whole save block for this run
        const follow = root.querySelector('.cquiz__follow');
        if (follow && follow.querySelector('.cquiz__success')) follow.hidden = true;
        try {
          sessionStorage.removeItem('fs-camera-quiz');
        } catch (e) {}
        show(0, true);
      });
    }

    // After the optional form posts, Shopify reloads the page with
    // ?customer_posted=true - restore the result so the confirmation has its
    // context. If sessionStorage is gone (new tab), still surface the result
    // shell so the confirmation and phone line are visible.
    let restored = false;
    const serverFormState = root.querySelector('.cquiz__success, .cquiz__form-error');
    if (window.location.search.indexOf('customer_posted=true') !== -1 || serverFormState) {
      const saved = safeParse((() => {
        try {
          return sessionStorage.getItem('fs-camera-quiz');
        } catch (e) {
          return null;
        }
      })());
      if (saved && saved.use) {
        Object.assign(answers, saved);
        recorded = true; // this completion was captured before the form posted
        renderResult();
        try {
          const pc = sessionStorage.getItem('fs-camera-quiz-pc');
          const pcInput = root.querySelector('.cquiz__postcode input');
          if (pc && pcInput && !pcInput.value) pcInput.value = pc;
        } catch (e) {}
        restored = true;
      } else if (serverFormState) {
        const card = root.querySelector('.cquiz__result-card');
        if (card) card.hidden = true;
        const bodyInput = root.querySelector('.cquiz__body-input');
        if (bodyInput) bodyInput.value = 'Camera quiz enquiry - answers unavailable (resubmitted after an error).';
        showResultShell();
        root.scrollIntoView({ block: 'start' });
        restored = true;
      }
    }

    if (!restored) show(0, false);
  }

  document.querySelectorAll('[data-camera-quiz]').forEach(initQuiz);
  document.addEventListener('shopify:section:load', (e) => {
    const r = e.target.querySelector('[data-camera-quiz]');
    if (r) initQuiz(r);
  });
})();
